import React, { useState, useEffect, useCallback } from "react";
import VerticalCategories from "./VerticalCategories";
import axiosInstance, { CMS_BASE_URL } from "../../../../../axios";
import PatientLoader from "../../../../common/PatientLoader";
import HorizontalMediaType from "./HorizontalMediaType";
import ContentTile from "./ContentTile";

const PinNewContentV2 = ({ patientID }) => {
  const [content, setContent] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [media] = useState([
    {
      label: "Videos",
      value: "Videos",
    },
    {
      label: "Audio",
      value: "Audio",
    },
    {
      label: "Article",
      value: "Article",
    },
  ]);

  //* Selected Data Section
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [currentTab, setCurrentTab] = useState(1);

  const isMediaMatch = (mediaType, selectedLabel) => {
    if (!mediaType || !selectedLabel) return false;
    const type = String(mediaType).toLowerCase().trim();
    const target = String(selectedLabel).toLowerCase().trim();
    if (target === "videos") {
      return ["videos", "video", "viewmedica", "view-medica", "view-medicas"].includes(type);
    }
    if (target === "audio") {
      return ["audio", "audios"].includes(type);
    }
    if (target === "article") {
      return ["article", "articles", "post", "posts"].includes(type);
    }
    return type === target;
  };

  const normalizeAppType = (val) => {
    if (!val) return null;
    if (typeof val === "object") {
      return val.App || val.app || null;
    }
    if (val === 1 || val === "1") return "Youth";
    if (val === 2 || val === "2") return "Adult";
    if (val === 3 || val === "3") return "YouthAndAdult";
    if (typeof val === "string") return val.trim();
    return null;
  };

  const isAppTypeAllowed = (rawAppType, dashboardType) => {
    const norm = normalizeAppType(rawAppType);
    if (!norm) return true;
    if (dashboardType === "youth") {
      return norm !== "Adult";
    } else {
      return norm !== "Youth";
    }
  };

  const MEDIA_LOOKUP = {
    1: media[0],
    2: media[1],
    3: media[2],
  };

  useEffect(() => {
    setSelectedMedia(MEDIA_LOOKUP[currentTab]);
  }, [currentTab]);

  const fetchAndFormatClassifications = useCallback(async () => {
    setLoading(true);
    const dashboardType = process.env.REACT_APP_DASHBOARD_TYPE || "youth";

    try {
      // 1. Fetch from classifications API (with isPinable=true to include pinnable categories like Meditations)
      const res = await axiosInstance.get(`${CMS_BASE_URL}/classifications/`, {
        params: { isPinable: "true" },
      });
      let rawClassifications = Array.isArray(res?.data) ? [...res.data] : [];

      // 2. Ensure Meditations / Audios are available so providers can always pin them
      const hasMeditationsWithAudios = rawClassifications.some(
        (c) =>
          (c?.Name || c?.name || "").toLowerCase().includes("meditation") &&
          Array.isArray(c?.audios) &&
          c.audios.length > 0
      );

      if (!hasMeditationsWithAudios) {
        try {
          const audioRes = await axiosInstance.get(`${CMS_BASE_URL}/content-type/audios/`);
          const audioList = Array.isArray(audioRes?.data) ? audioRes.data : [];
          if (audioList.length > 0) {
            const existingMedIndex = rawClassifications.findIndex((c) =>
              (c?.Name || c?.name || "").toLowerCase().includes("meditation")
            );
            if (existingMedIndex !== -1) {
              rawClassifications[existingMedIndex] = {
                ...rawClassifications[existingMedIndex],
                isPinable: true,
                audios: audioList,
              };
            } else {
              rawClassifications.push({
                id: 27,
                Name: "Meditations",
                name: "Meditations",
                isVisible: true,
                isVisibleYouth: true,
                isPinable: true,
                audios: audioList,
                posts: [],
                view_medicas: [],
                videos: [],
              });
            }
          }
        } catch (audioErr) {
          console.warn("Could not fetch standalone audios fallback:", audioErr);
        }
      }

      // 3. Filter classifications and normalize nested content
      const tempCategories = [];
      const allNormalizedContent = [];

      rawClassifications.forEach((cat) => {
        const cname = cat?.Name || cat?.name || "";
        const isPinable = cat?.isPinable;
        const isVisibleYouth = cat?.isVisibleYouth;

        // Skip non-pinnable classifications
        if (isPinable === false || isPinable === "false") {
          return;
        }

        // For youth dashboard, filter classifications by isVisibleYouth
        // Explicitly retain Meditations so providers can pin meditation/audios
        if (dashboardType === "youth") {
          const isMeditation = cname.toLowerCase().includes("meditation");
          if (!isMeditation && (isVisibleYouth === false || isVisibleYouth === "false")) {
            return;
          }
        }

        const catItems = [];

        // Posts -> Article
        (cat?.posts || []).forEach((item) => {
          if (isAppTypeAllowed(item?.app_type, dashboardType)) {
            catItems.push({
              ...item,
              media_type: "Article",
              classification: { id: cat.id, Name: cname, name: cname },
            });
          }
        });

        // ViewMedicas / Videos -> Videos
        [...(cat?.view_medicas || []), ...(cat?.videos || [])].forEach((item) => {
          if (isAppTypeAllowed(item?.app_type, dashboardType)) {
            catItems.push({
              ...item,
              media_type: "Videos",
              classification: { id: cat.id, Name: cname, name: cname },
            });
          }
        });

        // Audios -> Audio
        (cat?.audios || []).forEach((item) => {
          if (isAppTypeAllowed(item?.app_type, dashboardType)) {
            catItems.push({
              ...item,
              media_type: "Audio",
              classification: { id: cat.id, Name: cname, name: cname },
            });
          }
        });

        if (catItems.length > 0) {
          tempCategories.push({
            label: cname,
            value: cname,
            id: cat?.id,
          });
          allNormalizedContent.push(...catItems);
        }
      });

      setCategories(tempCategories);
      setData(allNormalizedContent);
    } catch (error) {
      console.error("Error fetching classifications for pinned content:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAndFormatClassifications();
  }, [fetchAndFormatClassifications]);

  useEffect(() => {
    if (categories.length > 0) {
      setSelectedCategory(categories[0]);
      setSelectedMedia(media[0]);
    }
  }, [categories]);

  useEffect(() => {
    if (selectedCategory && selectedMedia) {
      const temp = [];
      data.forEach((contentItem) => {
        const itemTitle = contentItem?.Title || contentItem?.title;
        const itemMediaType = contentItem?.media_type || contentItem?.mediaType;
        const itemClassName = contentItem?.classification?.Name || contentItem?.classification?.name;
        if (
          isMediaMatch(itemMediaType, selectedMedia?.label) &&
          itemClassName === selectedCategory?.label
        ) {
          temp.push({
            label: itemTitle,
            value: contentItem,
          });
        }
      });
      setContent(temp);
    }
  }, [selectedCategory, selectedMedia, data]);

  return (
    <div className="container-fluid p-2 my-3">
      {loading ? (
        <PatientLoader />
      ) : (
        <div className="row">
          <div className="col-sm-3 col-md-3 col-lg-3">
            <VerticalCategories
              categories={categories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          </div>
          <div
            className="col-sm-9 col-md-9 col-lg-9"
            style={{ borderLeft: "1px solid lightgray" }}
          >
            <h4>{selectedCategory && selectedCategory?.value}</h4>
            <HorizontalMediaType
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
            />
            <div
              className="row container-fluid w-100 my-2 py-2"
              style={{ height: "60dvh", overflowY: "scroll" }}
            >
              {content?.length > 0 ? (
                content.map((item, index) => {
                  return (
                    <div className="col-sm-12 col-md-4 col-lg-4" key={index}>
                      <ContentTile content_data={item} patientID={patientID} />
                    </div>
                  );
                })
              ) : (
                <div className="col-12 text-center text-muted py-5">
                  <p>
                    No {selectedMedia?.label?.toLowerCase() || "content"} available in{" "}
                    {selectedCategory?.label}.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PinNewContentV2;
